export type RuntimeConfig = {
  apiUrl?: string;
  apiUrlsByEnvironment?: Record<string, string>;
  apiUrlsByHost?: Record<string, string>;
  apiUrlOptions?: unknown;
  environment?: string;
  imageBaseUrl?: string;
  agGridLicenseKey?: string;
  muiLicenseKey?: string;
};

const isBrowser = typeof window !== "undefined";

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: unknown;
    __ENV__?: unknown;
  }
}

const normalizeStringMap = (
  value?: unknown,
  keyTransformer: (key: string) => string = (key) => key.toLowerCase(),
): Record<string, string> | undefined => {
  if (!value || typeof value !== "object") {
    return undefined;
  }

  const entries = Object.entries(value).filter(
    ([key, mapValue]) =>
      typeof key === "string" &&
      key.trim().length > 0 &&
      typeof mapValue === "string" &&
      mapValue.trim().length > 0,
  );

  if (entries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(
    entries.map(([key, mapValue]) => [keyTransformer(key), mapValue]),
  );
};

const normalizeRuntimeConfig = (rawConfig: unknown): RuntimeConfig => {
  if (!rawConfig || typeof rawConfig !== "object") {
    return {};
  }

  const config = rawConfig as Record<string, unknown>;
  const normalized: RuntimeConfig = {};

  if (typeof config.apiUrl === "string") {
    normalized.apiUrl = config.apiUrl;
  }

  if (
    config.apiUrlsByEnvironment &&
    typeof config.apiUrlsByEnvironment === "object"
  ) {
    normalized.apiUrlsByEnvironment = normalizeStringMap(
      config.apiUrlsByEnvironment,
    );
  }

  const runtimeHosts = normalizeStringMap(
    (config.apiUrlsByHost as Record<string, unknown>) ??
      (config.apiUrlsByHost as unknown),
  );

  if (runtimeHosts) {
    normalized.apiUrlsByHost = runtimeHosts;
  }

  if (typeof config.imageBaseUrl === "string") {
    normalized.imageBaseUrl = config.imageBaseUrl;
  }

  if (
    typeof config.apiUrlOptions === "string" ||
    Array.isArray(config.apiUrlOptions)
  ) {
    normalized.apiUrlOptions = config.apiUrlOptions;
  }

  if (typeof config.environment === "string") {
    normalized.environment = config.environment;
  }

  if (typeof config.agGridLicenseKey === "string") {
    normalized.agGridLicenseKey = config.agGridLicenseKey;
  }

  if (typeof config.muiLicenseKey === "string") {
    normalized.muiLicenseKey = config.muiLicenseKey;
  }

  return normalized;
};

const normalizeEnvironmentConfig = (rawConfig: unknown): RuntimeConfig => {
  if (!rawConfig || typeof rawConfig !== "object") {
    return {};
  }

  const config = rawConfig as Record<string, unknown>;
  const normalized: RuntimeConfig = {};

  const apiUrl =
    typeof config.apiUrl === "string"
      ? config.apiUrl
      : typeof config.VITE_API_URL === "string"
        ? config.VITE_API_URL
        : undefined;

  if (apiUrl) {
    normalized.apiUrl = apiUrl;
  }

  const apiUrlOptions =
    config.apiUrlOptions ?? config.VITE_API_URL_OPTIONS ?? undefined;

  if (
    typeof apiUrlOptions === "string" ||
    Array.isArray(apiUrlOptions)
  ) {
    normalized.apiUrlOptions = apiUrlOptions;
  }

  if (
    config.apiUrlsByEnvironment &&
    typeof config.apiUrlsByEnvironment === "object"
  ) {
    normalized.apiUrlsByEnvironment = normalizeStringMap(
      config.apiUrlsByEnvironment,
    );
  }

  const envHosts =
    normalizeStringMap(
      (config.apiUrlsByHost as Record<string, unknown>) ??
        (config.VITE_API_URLS_BY_HOST as Record<string, unknown>),
    ) ?? normalizeStringMap(config.VITE_API_URLS_BY_HOST as unknown);

  if (envHosts) {
    normalized.apiUrlsByHost = envHosts;
  }

  const environment =
    typeof config.environment === "string"
      ? config.environment
      : typeof config.ASPNETCORE_ENVIRONMENT === "string"
        ? config.ASPNETCORE_ENVIRONMENT
      : typeof config.VITE_ENVIRONMENT === "string"
        ? config.VITE_ENVIRONMENT
        : undefined;

  const imageBaseUrl =
    typeof config.imageBaseUrl === "string"
      ? config.imageBaseUrl
      : typeof config.VITE_IMAGE_BASE_URL === "string"
        ? config.VITE_IMAGE_BASE_URL
        : undefined;

  if (environment) {
    normalized.environment = environment;
  }

  if (imageBaseUrl) {
    normalized.imageBaseUrl = imageBaseUrl;
  }

  const agGridLicenseKey =
    typeof config.agGridLicenseKey === "string"
      ? config.agGridLicenseKey
      : typeof config.VITE_AG_GRID_KEY === "string"
        ? config.VITE_AG_GRID_KEY
        : undefined;

  if (agGridLicenseKey) {
    normalized.agGridLicenseKey = agGridLicenseKey;
  }

  const muiLicenseKey =
    typeof config.muiLicenseKey === "string"
      ? config.muiLicenseKey
      : typeof config.VITE_MUI_LICENSE_KEY === "string"
        ? config.VITE_MUI_LICENSE_KEY
        : undefined;

  if (muiLicenseKey) {
    normalized.muiLicenseKey = muiLicenseKey;
  }

  return normalized;
};

const runtimeConfig = normalizeRuntimeConfig(
  isBrowser ? window.__RUNTIME_CONFIG__ : undefined,
);

const environmentConfig = normalizeEnvironmentConfig(
  isBrowser ? window.__ENV__ : undefined,
);

export const getRuntimeConfig = (): RuntimeConfig => runtimeConfig;

export const getEnvironmentConfig = (): RuntimeConfig => environmentConfig;
